'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Timer, BookOpen, Layers, Hash } from 'lucide-react';
import { CategoryIcon } from '@/components/ui/CategoryIcon';
import { useQuizStore } from '@/store/useQuizStore';
import { generateQuizQuestions } from '@/lib/quiz';
import { getAllQuestions } from '@/lib/questions';
import type { Category, QuizConfig, CategorySlug, DifficultyLevel } from '@/types';

interface Props {
  categories: Category[];
}

export function QuizSetupForm({ categories }: Props) {
  const router = useRouter();
  const initQuiz = useQuizStore((s) => s.initQuiz);

  const [category, setCategory] = useState<CategorySlug | 'mixed'>('mixed');
  const [diffMin, setDiffMin] = useState<DifficultyLevel>(1);
  const [diffMax, setDiffMax] = useState<DifficultyLevel>(5);
  const [questionCount, setQuestionCount] = useState<5 | 10 | 20 | 30>(10);
  const [timedMode, setTimedMode] = useState(true);
  const [secondsPerQuestion, setSecondsPerQuestion] = useState(30);

  const handleStart = () => {
    const config: QuizConfig = {
      category,
      difficultyMin: diffMin,
      difficultyMax: diffMax,
      questionCount,
      timedMode,
      secondsPerQuestion,
    };

    const allQuestions = getAllQuestions();
    const quizQuestions = generateQuizQuestions(allQuestions, config);

    if (quizQuestions.length === 0) {
      alert('Not enough questions for this filter. Try a different category or difficulty range.');
      return;
    }

    initQuiz(config, quizQuestions);
    router.push('/quiz/play');
  };

  const diffLabels: Record<number, string> = {
    1: 'Beginner',
    2: 'Easy',
    3: 'Medium',
    4: 'Hard',
    5: 'Expert',
  };

  return (
    <div className="card-base p-6 md:p-8 space-y-8">
      {/* Category */}
      <div>
        <label className="flex items-center gap-2 font-semibold text-sm mb-3" style={{ color: 'var(--color-text-primary)' }}>
          <BookOpen size={16} />
          Category
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <button
            onClick={() => setCategory('mixed')}
            className="px-3 py-2 rounded-lg text-sm font-medium transition-all text-center"
            style={
              category === 'mixed'
                ? { background: 'var(--color-primary)', color: '#fff' }
                : { background: 'var(--color-surface)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
            }
          >
            🎲 Mixed
          </button>
          {categories.sort((a, b) => a.order - b.order).map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setCategory(cat.slug)}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-all text-left truncate"
              style={
                category === cat.slug
                  ? { background: cat.color, color: '#fff' }
                  : { background: 'var(--color-surface)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
              }
            >
              <CategoryIcon name={cat.icon} size={14} />
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Difficulty Range */}
      <div>
        <label className="flex items-center gap-2 font-semibold text-sm mb-3" style={{ color: 'var(--color-text-primary)' }}>
          <Layers size={16} />
          Difficulty Range
        </label>
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>Min: {diffLabels[diffMin]}</p>
            <div className="flex gap-1">
              {([1, 2, 3, 4, 5] as DifficultyLevel[]).map((d) => (
                <button
                  key={d}
                  onClick={() => setDiffMin(Math.min(d, diffMax) as DifficultyLevel)}
                  className="flex-1 h-8 rounded-lg text-xs font-bold transition-all"
                  style={
                    d <= diffMin
                      ? { background: 'var(--color-primary)', color: '#fff' }
                      : { background: 'var(--color-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }
                  }
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <span style={{ color: 'var(--color-text-muted)' }}>→</span>
          <div className="flex-1">
            <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>Max: {diffLabels[diffMax]}</p>
            <div className="flex gap-1">
              {([1, 2, 3, 4, 5] as DifficultyLevel[]).map((d) => (
                <button
                  key={d}
                  onClick={() => setDiffMax(Math.max(d, diffMin) as DifficultyLevel)}
                  className="flex-1 h-8 rounded-lg text-xs font-bold transition-all"
                  style={
                    d <= diffMax
                      ? { background: 'var(--color-secondary)', color: '#fff' }
                      : { background: 'var(--color-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }
                  }
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Question Count */}
      <div>
        <label className="flex items-center gap-2 font-semibold text-sm mb-3" style={{ color: 'var(--color-text-primary)' }}>
          <Hash size={16} />
          Number of Questions
        </label>
        <div className="flex gap-2">
          {([5, 10, 20, 30] as const).map((n) => (
            <button
              key={n}
              onClick={() => setQuestionCount(n)}
              className="flex-1 py-2 rounded-lg text-sm font-bold transition-all"
              style={
                questionCount === n
                  ? { background: 'var(--color-primary)', color: '#fff' }
                  : { background: 'var(--color-surface)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }
              }
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Timed Mode */}
      <div>
        <label className="flex items-center gap-2 font-semibold text-sm mb-3" style={{ color: 'var(--color-text-primary)' }}>
          <Timer size={16} />
          Timed Mode
        </label>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setTimedMode((t) => !t)}
            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
            style={{ background: timedMode ? 'var(--color-primary)' : 'var(--color-border)' }}
            role="switch"
            aria-checked={timedMode}
          >
            <span
              className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
              style={{ transform: timedMode ? 'translateX(1.25rem)' : 'translateX(0.25rem)' }}
            />
          </button>
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {timedMode ? 'On' : 'Off'}
          </span>

          {timedMode && (
            <div className="flex items-center gap-2 ml-4">
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Seconds per question:</span>
              {[15, 30, 60].map((s) => (
                <button
                  key={s}
                  onClick={() => setSecondsPerQuestion(s)}
                  className="px-2 py-1 rounded text-xs font-medium transition-all"
                  style={
                    secondsPerQuestion === s
                      ? { background: 'var(--color-primary)', color: '#fff' }
                      : { background: 'var(--color-surface)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }
                  }
                >
                  {s}s
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Start Button */}
      <button
        onClick={handleStart}
        className="btn-primary w-full py-4 text-lg font-bold"
      >
        Start Quiz →
      </button>
    </div>
  );
}
